import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';

import { ENestedObjectType } from '@/components/common/react-drag-hierarchy-tree-main/src/interfaces';
import { PSButton } from '@/components/ui/button';

import DeleteButton from '../../DeleteButton';
import { TextScenarioType } from '../scenario-constant';
import { acceptableBlocksMap } from './text-scenario.component';

type Props = {
  itemData: any;
  tree: any[];
  deleteHandler: (e?: any) => void;
};

export default function TextScenarioDeleteButton({ itemData, tree, deleteHandler }: Props) {
  const [showDelete, setShowDelete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const [parent, setParent] = useState<any>(null);
  const [child, setChild] = useState<any>();
  const [hide, setHide] = useState(false);

  useEffect(() => undefined, []);
  // Helper to find node by id
  const findNodeById = (id: string | null) => tree.find(node => node.id === id);

  useEffect(() => {
    if (!itemData?.id) {
      setShowDelete(false);
      setHide(true);
      return;
    }

    setHide(false);
    setShowDelete(false);

    const currentNode = findNodeById(itemData.id);
    if (!currentNode) {
      setShowDelete(false);
      return;
    }

    if (currentNode.type == 1 && currentNode.parent == null) {
      setHide(true);
    }

    const parentNode = findNodeById(currentNode.parent);
    const firstChild = currentNode.children?.[0];

    if (firstChild && parentNode) {
      setParent(parentNode);
      setChild(firstChild);
      const allowedTypes = (acceptableBlocksMap as any)[parentNode.type] || [];
      const firstChildTypeName = (TextScenarioType as any)[firstChild.type];
      setShowDelete(allowedTypes.includes(firstChildTypeName));
    } else {
      // If no children, allow delete
      setShowDelete(true);
    }
  }, [tree, itemData]);

  const getTitle = (node: any) => {
    if (node && node.type && (node.type == ENestedObjectType.Goto || node.type == ENestedObjectType.VideoGoto)) {
      return 'Goto ' + node?.block_id;
    }

    return node?.title;
  };

  return (
    <>
      {!hide && (
        <>
          {showDelete ? <DeleteButton deleteHandler={deleteHandler} /> : <DeleteButton deleteHandler={() => setShowModal(true)} />}

          {showModal && (
            <Modal
              contentClassName="tw-bg-white tw-rounded-2xl tw-shadow tw-flex-col tw-justify-start tw-items-center tw-inline-flex"
              show={showModal}
              onHide={() => setShowModal(false)}
              backdrop="static"
              centered
              size="sm"
            >
              <Modal.Body className="tw-flex tw-flex-col tw-gap-y-4">
                <div className="tw-self-stretch tw-px-4 tw-pt-5 tw-pb-4 tw-flex-col tw-justify-start tw-items-center tw-gap-3 tw-flex">
                  <div className="tw-w-10 tw-h-10 tw-p-2.5 tw-bg-amber-100 tw-rounded-3xl tw-justify-center tw-items-center tw-inline-flex">
                    <BsFillExclamationTriangleFill size={20} className="tw-text-amber-500" />
                  </div>
                  <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-center tw-gap-2 tw-flex">
                    <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-md tw-font-['Lexend'] tw-leading-7">
                      {t('DeleteScenario/message', {
                        current: getTitle(itemData),
                        parent: getTitle(parent),
                        child: getTitle(child),
                      })}
                    </div>
                  </div>
                  <div className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-gray-50 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                    <PSButton id="btn-cancel-delete" variant="outlineWarning" onClick={() => setShowModal(false)} title={t('DeleteModal/key2')} />

                    <PSButton
                      id="btn-delete"
                      variant="warning"
                      onClick={() => {
                        setShowModal(false);
                        deleteHandler();
                      }}
                      title={t('DeleteScenario/tryAnyway')}
                    />
                  </div>
                </div>
              </Modal.Body>
            </Modal>
          )}
        </>
      )}
    </>
  );
}
